// src/components/microphone/DirectAudioPlayer.js
import React, { useEffect, useRef, useState } from 'react';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import './DirectAudioPlayer.css';

/**
 * DirectAudioPlayer component using AudioWorklet for real-time PCM streaming
 * Includes waveform visualization of incoming audio data and inline worklet definition
 */
const DirectAudioPlayer = ({ audioData, waveformData }) => {
  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5); // Lower default volume
  const [isMuted, setIsMuted] = useState(false);
  const [bufferCount, setBufferCount] = useState(0);
  const [isWorkletReady, setIsWorkletReady] = useState(false);
  const [displayWaveform, setDisplayWaveform] = useState([]);
  const [workletError, setWorkletError] = useState(null);
  
  // Refs for audio processing
  const audioContextRef = useRef(null);
  const gainNodeRef = useRef(null);
  const workletNodeRef = useRef(null);
  const workletReadyRef = useRef(false);
  const wasPlayingRef = useRef(false);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  // Update waveform when new data comes in
  useEffect(() => {
    if (waveformData && Array.isArray(waveformData) && waveformData.length > 0) {
      setDisplayWaveform(waveformData);
    }
  }, [waveformData]);
  
  // Initialize the audio context and load worklet on component mount
  useEffect(() => {
    const init = async () => {
      try {
        // Create the audio context with low latency settings
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContext({
          latencyHint: 'interactive',
          sampleRate: 16000
        });
        
        // Create gain node for volume control
        gainNodeRef.current = audioContextRef.current.createGain();
        gainNodeRef.current.gain.value = volume;
        gainNodeRef.current.connect(audioContextRef.current.destination);
        
        // Define the PCM processor worklet code as a blob
        const workletCode = `
          class PCMProcessor extends AudioWorkletProcessor {
            constructor() {
              super();
              
              // Buffer for processing audio
              this.pcmBuffer = null;
              this.bufferOffset = 0;
              this.bufferLength = 0;
              
              // Audio processing settings
              this.gainFactor = 0.5;
              this.smoothingEnabled = true;
              this.smoothingFactor = 0.7;
              this.compressionEnabled = true;
              this.compressionThreshold = 0.3;
              this.compressionRatio = 3.0;
              
              // For visualization
              this.lastSamples = new Float32Array(128).fill(0);
              
              // Message handling from main thread
              this.port.onmessage = (event) => {
                const { type } = event.data;
                
                if (type === 'pcm-data') {
                  // New PCM data to process
                  const { buffer } = event.data;
                  
                  // Convert ArrayBuffer to Int16Array (common PCM format)
                  const int16Data = new Int16Array(buffer);
                  
                  // Create a Float32Array with normalized values (-1.0 to 1.0)
                  this.pcmBuffer = new Float32Array(int16Data.length);
                  
                  // Convert Int16 PCM to Float32 (-1 to 1)
                  for (let i = 0; i < int16Data.length; i++) {
                    this.pcmBuffer[i] = int16Data[i] / 32768.0;
                  }
                  
                  // Reset buffer position
                  this.bufferOffset = 0;
                  this.bufferLength = this.pcmBuffer.length;
                  
                  // Send visualization data back to main thread
                  this.sendVisualizationData();
                } else if (type === 'config') {
                  // Update configuration
                  const { 
                    gainFactor, 
                    smoothingEnabled, 
                    compressionEnabled,
                    compressionThreshold,
                    compressionRatio
                  } = event.data;
                  
                  if (gainFactor !== undefined) this.gainFactor = gainFactor;
                  if (smoothingEnabled !== undefined) this.smoothingEnabled = smoothingEnabled;
                  if (compressionEnabled !== undefined) this.compressionEnabled = compressionEnabled;
                  if (compressionThreshold !== undefined) this.compressionThreshold = compressionThreshold;
                  if (compressionRatio !== undefined) this.compressionRatio = compressionRatio;
                }
              };
            }
            
            // Send visualization data back to main thread
            sendVisualizationData() {
              if (!this.pcmBuffer || this.pcmBuffer.length === 0) return;
              
              // Get a downsampled version of the buffer for visualization
              const numSamples = Math.min(128, this.pcmBuffer.length);
              const visualData = new Float32Array(numSamples);
              
              const step = Math.max(1, Math.floor(this.pcmBuffer.length / numSamples));
              for (let i = 0; i < numSamples; i++) {
                const idx = Math.min(i * step, this.pcmBuffer.length - 1);
                visualData[i] = this.pcmBuffer[idx];
              }
              
              // Send visualization data to main thread
              this.port.postMessage({
                type: 'visualization',
                data: visualData
              });
            }
            
            // Apply compression to a sample
            compressSample(sample) {
              if (!this.compressionEnabled) return sample;
              
              const threshold = this.compressionThreshold;
              const ratio = this.compressionRatio;
              const absValue = Math.abs(sample);
              
              if (absValue <= threshold) {
                return sample;
              } else {
                const compressedMagnitude = threshold + (absValue - threshold) / ratio;
                return sample > 0 ? compressedMagnitude : -compressedMagnitude;
              }
            }
            
            // Main audio processing function
            process(inputs, outputs, parameters) {
              const output = outputs[0];
              
              // If we have no PCM buffer or have reached the end, output silence
              if (!this.pcmBuffer || this.bufferOffset >= this.bufferLength) {
                for (let channel = 0; channel < output.length; channel++) {
                  const outputChannel = output[channel];
                  for (let i = 0; i < outputChannel.length; i++) {
                    outputChannel[i] = 0;
                  }
                }
                return true;
              }
              
              // Get the first output channel
              const outputChannel = output[0];
              
              // Copy PCM data to output with processing
              for (let i = 0; i < outputChannel.length; i++) {
                if (this.bufferOffset < this.bufferLength) {
                  // Get next PCM sample
                  let sample = this.pcmBuffer[this.bufferOffset++] * this.gainFactor;
                  
                  // Apply compression
                  sample = this.compressSample(sample);
                  
                  // Apply smoothing if enabled
                  if (this.smoothingEnabled && i < this.lastSamples.length) {
                    sample = this.lastSamples[i] * this.smoothingFactor + sample * (1 - this.smoothingFactor);
                    this.lastSamples[i] = sample;
                  }
                  
                  // Write to all output channels
                  for (let channel = 0; channel < output.length; channel++) {
                    output[channel][i] = sample;
                  }
                } else {
                  // We've reached the end of our buffer, output silence
                  for (let channel = 0; channel < output.length; channel++) {
                    output[channel][i] = 0;
                  }
                }
              }
              
              return true;
            }
          }
          
          registerProcessor('pcm-processor', PCMProcessor);
        `;
        
        // Create a blob URL from the worklet code
        const blob = new Blob([workletCode], { type: 'application/javascript' });
        const workletUrl = URL.createObjectURL(blob);
        
        console.log('Loading AudioWorklet from blob URL:', workletUrl);
        
        // Load the AudioWorklet processor from the blob URL
        await audioContextRef.current.audioWorklet.addModule(workletUrl);
        
        // Create the AudioWorkletNode
        const workletNode = new AudioWorkletNode(audioContextRef.current, 'pcm-processor');
        workletNodeRef.current = workletNode;
        workletNode.connect(gainNodeRef.current);
        
        // Now safe to use port
        workletNodeRef.current.port.postMessage({
          type: 'config',
          gainFactor: 0.6,
          smoothingEnabled: true,
          compressionEnabled: true,
          compressionThreshold: 0.3,
          compressionRatio: 3
        });
        
        // Clean up the blob URL
        URL.revokeObjectURL(workletUrl);
        
        // Mark worklet as ready
        workletReadyRef.current = true;
        setIsWorkletReady(true);
        setWorkletError(null);
        
        console.log('Audio context and worklet initialized with sample rate:', audioContextRef.current.sampleRate);
      } catch (err) {
        console.error('Error initializing audio worklet:', err);
        setWorkletError(err.message);
        
        // Fallback to a more basic audio approach if worklet fails
        try {
          if (!gainNodeRef.current && audioContextRef.current) {
            gainNodeRef.current = audioContextRef.current.createGain();
            gainNodeRef.current.gain.value = volume;
            gainNodeRef.current.connect(audioContextRef.current.destination);
          }
        } catch (fallbackErr) {
          console.error('Error creating fallback audio pipeline:', fallbackErr);
        }
      }
    };
    
    init();
    
    return () => {
      // Clean up on unmount
      if (workletNodeRef.current) {
        workletNodeRef.current.disconnect();
      }
      
      if (gainNodeRef.current) {
        gainNodeRef.current.disconnect();
      }
      
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [volume]);
  
  // Handle volume and mute changes
  useEffect(() => {
    if (gainNodeRef.current && audioContextRef.current) {
      // Use a slight ramp to avoid clicks
      const now = audioContextRef.current.currentTime;
      gainNodeRef.current.gain.cancelScheduledValues(now);
      gainNodeRef.current.gain.setValueAtTime(gainNodeRef.current.gain.value, now);
      gainNodeRef.current.gain.linearRampToValueAtTime(isMuted ? 0 : volume, now + 0.05);
    }
  }, [volume, isMuted]);
  
  // Process incoming audio data
  useEffect(() => {
    if (!audioData || !isPlaying || !audioContextRef.current) return;
    
    if (audioData instanceof ArrayBuffer) {
      // If worklet is ready, use it
      if (workletReadyRef.current && workletNodeRef.current) {
        // Send the PCM data to the worklet for processing
        workletNodeRef.current.port.postMessage({
          type: 'pcm-data',
          buffer: audioData
        });
      } else {
        // Fallback: Direct buffer playback if worklet fails
        try {
          const audioBuffer = audioContextRef.current.createBuffer(
            1, // mono
            audioData.byteLength / 2, // assuming 16-bit PCM
            audioContextRef.current.sampleRate
          );
          
          // Convert ArrayBuffer to Float32Array for audio buffer
          const int16View = new Int16Array(audioData);
          const float32Array = new Float32Array(int16View.length);
          
          // Convert Int16 PCM to normalized Float32 (-1 to 1)
          for (let i = 0; i < int16View.length; i++) {
            float32Array[i] = int16View[i] / 32768.0;
          }
          
          // Fill channel data
          audioBuffer.getChannelData(0).set(float32Array);
          
          // Create source and play
          const source = audioContextRef.current.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(gainNodeRef.current);
          source.start();
        } catch (err) {
          console.error('Error in fallback audio playback:', err);
        }
      }
      
      // Update buffer count for display
      setBufferCount(prev => prev + 1);
    }
  }, [audioData, isPlaying]);
  
  // Draw waveform visualization
  useEffect(() => {
    if (!canvasRef.current || displayWaveform.length === 0) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Draw function
    const drawWaveform = () => {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);
      
      // Draw waveform
      const centerY = height / 2;
      const scale = height / 2; // Scale to fit canvas height
      
      ctx.beginPath();
      ctx.moveTo(0, centerY);
      
      // Draw the waveform line
      const step = width / displayWaveform.length;
      for (let i = 0; i < displayWaveform.length; i++) {
        const x = i * step;
        // Waveform values are typically in range -0.5 to 0.5
        const y = centerY - (displayWaveform[i] * scale);
        ctx.lineTo(x, y);
      }
      
      // Set line style
      ctx.strokeStyle = isPlaying ? '#2196f3' : '#90caf9';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Continue animation if playing
      if (isPlaying) {
        animationFrameRef.current = requestAnimationFrame(drawWaveform);
      }
    };
    
    // Start drawing
    drawWaveform();
    
    // Clean up animation
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [displayWaveform, isPlaying]);
  
  // Effect to resume audio context when needed
  useEffect(() => {
    const resumeContext = async () => {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        try {
          await audioContextRef.current.resume();
          console.log('Audio context resumed');
          
          // If we were playing before suspension, restore that state
          if (wasPlayingRef.current) {
            setIsPlaying(true);
          }
        } catch (err) {
          console.error('Error resuming audio context:', err);
        }
      }
    };
    
    // Try to resume on user interaction or if playing changes
    window.addEventListener('click', resumeContext);
    window.addEventListener('touchstart', resumeContext);
    
    // Save playing state when context gets suspended
    const handleContextStateChange = () => {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        wasPlayingRef.current = isPlaying;
        if (isPlaying) {
          setIsPlaying(false);
        }
      }
    };
    
    if (audioContextRef.current) {
      audioContextRef.current.addEventListener('statechange', handleContextStateChange);
    }
    
    return () => {
      window.removeEventListener('click', resumeContext);
      window.removeEventListener('touchstart', resumeContext);
      if (audioContextRef.current) {
        audioContextRef.current.removeEventListener('statechange', handleContextStateChange);
      }
    };
  }, [isPlaying]);
  
  // Toggle play/pause
  const togglePlayback = async () => {
    try {
      if (isPlaying) {
        // Stop playback
        setIsPlaying(false);
        console.log('Audio playback stopped');
        
        // Cancel animation
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      } else {
        // Make sure context is running
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }
        
        // Start playback
        setIsPlaying(true);
        console.log('Audio playback started');
      }
    } catch (err) {
      console.error('Error toggling playback:', err);
    }
  };
  
  // Handle volume change
  const handleVolumeChange = (e) => {
    setVolume(parseFloat(e.target.value));
  };
  
  // Toggle mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };
  
  // Check if audio system is ready
  const isReady = audioContextRef.current && (isWorkletReady || workletError);
  const hasWaveform = displayWaveform && displayWaveform.length > 0;
  
  return (
    <div className="audio-player">
      <div className="audio-player-header">
        <div className="audio-player-status">
          <span className={`status-dot ${isReady ? 'active' : 'inactive'}`}></span>
          <span className="status-text">
            {!isReady ? 'Initializing Audio System...' : 
             workletError ? `Audio Ready (Basic Mode)` :
             isPlaying ? `Streaming Audio (${bufferCount} buffers received)` : 
             'Audio Ready'}
          </span>
        </div>
      </div>
      
      {/* Waveform Visualization */}
      <div className="waveform-container">
        <canvas 
          ref={canvasRef}
          width={300}
          height={80}
          className={`waveform-canvas ${hasWaveform ? 'has-data' : ''}`}
        />
        {!hasWaveform && <div className="no-waveform-message">No audio data available</div>}
      </div>
      
      <div className="audio-player-controls">
        <button 
          className={`play-pause-button ${isPlaying ? 'playing' : ''}`}
          onClick={togglePlayback}
          disabled={!isReady}
        >
          {isPlaying ? <FaPause /> : <FaPlay />}
        </button>
        
        <button
          className={`mute-button ${isMuted ? 'muted' : ''}`}
          onClick={toggleMute}
          disabled={!isReady || !isPlaying}
        >
          {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
        </button>
        
        <div className="volume-slider">
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
            disabled={!isReady || !isPlaying || isMuted}
          />
          <span className="volume-value">{Math.round(volume * 100)}%</span>
        </div>
      </div>
    </div>
  );
};

export default DirectAudioPlayer;